import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Card,
  Form,
  Input,
  Button,
  message,
  Select,
  Spin,
  Space,
  Breadcrumb
} from 'antd';
import { HomeOutlined, FileTextOutlined, SaveOutlined } from '@ant-design/icons';
import { blogAPI, categoryAPI } from '../utils/api';
import { useAuth } from '../hooks/useAuthHook';
import WangEditor from '../components/WangEditor';

const { TextArea } = Input;

const BlogEditor = () => {
  const { username } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [loading, setLoading] = useState(false);
  const [fetching, setFetching] = useState(false);
  const [categories, setCategories] = useState([]);
  const [blog, setBlog] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [form] = Form.useForm();

  const queryParams = new URLSearchParams(window.location.search);
  const blogId = queryParams.get('id');
  const isEdit = !!blogId;

  useEffect(() => {
    if (blogId) {
      fetchBlog(blogId);
    }
    fetchCategories();
  }, [blogId]);

  const fetchBlog = async (id) => {
    try {
      setFetching(true);
      const response = await blogAPI.getBlog(id);
      const blogData = response.data;
      if (!blogData) {
        message.warning('未找到该博客，可能已被删除');
        navigate(`/${username}/blog/`);
        return;
      }
      setBlog(blogData);
      form.setFieldsValue({
        title: blogData.title,
        summary: blogData.summary,
        content: blogData.content || '',
        categoryId: blogData.categoryId,
        status: blogData.status
      });
    } catch (error) {
      message.error('获取博客详情失败');
      navigate(`/${username}/blog/article/edit`);
    } finally {
      setFetching(false);
    }
  };

  const fetchCategories = async () => {
    try {
      const response = await categoryAPI.getCategoriesByAuthor(user.username);
      setCategories(response.data || []);
    } catch (error) {
      console.error('获取分类列表失败:', error);
    }
  };

  const handleSubmit = async (publish = false) => {
    try {
      const values = await form.validateFields();
      setSubmitting(true);

      const blogData = {
        ...values,
        status: publish ? 1 : 0,
        authorId: user.username
      };

      if (isEdit) {
        await blogAPI.updateBlog(blogId, blogData);
        message.success(publish ? '更新并发布成功' : '更新成功');
      } else {
        await blogAPI.createBlog(blogData);
        message.success(publish ? '创建并发布成功' : '创建成功');
      }

      navigate(`/${username}/blog/`);
    } catch (error) {
      console.error('保存失败:', error);
      message.error('保存失败');
    } finally {
      setSubmitting(false);
    }
  };

  const handleCancel = () => {
    if (form.isFieldsTouched()) {
      if (window.confirm('确定要放弃编辑吗？未保存的内容将丢失。')) {
        navigate(`/${username}/blog/`);
      }
    } else {
      navigate(`/${username}/blog/`);
    }
  };

  if (fetching) {
    return (
      <div style={{ textAlign: 'center', padding: 40 }}>
        <Spin size="large" tip="加载中..." />
      </div>
    );
  }

  return (
    <div style={{ width: '100%', maxWidth: 1000, margin: '0 auto', padding: 24 }}>
      <Breadcrumb style={{ marginBottom: 16 }}>
        <Breadcrumb.Item href="/">
          <HomeOutlined />
        </Breadcrumb.Item>
        <Breadcrumb.Item href={`/${username}/blog`}>
          <FileTextOutlined /> 博客
        </Breadcrumb.Item>
        <Breadcrumb.Item>
          {isEdit ? '编辑博客' : '新建博客'}
        </Breadcrumb.Item>
      </Breadcrumb>

      <Card
        title={isEdit ? '编辑博客' : '新建博客'}
        extra={
          <Space>
            <Button onClick={handleCancel}>
              取消
            </Button>
            <Button
              type="default"
              icon={<SaveOutlined />}
              loading={submitting}
              onClick={() => handleSubmit(false)}
            >
              {isEdit ? '保存草稿' : '保存草稿'}
            </Button>
            <Button
              type="primary"
              icon={<SaveOutlined />}
              loading={submitting}
              onClick={() => handleSubmit(true)}
            >
              {isEdit ? '更新并发布' : '创建并发布'}
            </Button>
          </Space>
        }
      >
        <Form
          form={form}
          layout="vertical"
          initialValues={{
            status: 0,
            categoryId: undefined
          }}
        >
          <Form.Item
            name="title"
            label="博客标题"
            rules={[
              { required: true, message: '请输入博客标题' },
              { max: 200, message: '标题不能超过200个字符' }
            ]}
          >
            <Input placeholder="请输入博客标题" size="large" />
          </Form.Item>

          <Form.Item
            name="categoryId"
            label="分类"
            rules={[{ required: true, message: '请选择分类' }]}
          >
            <Select
              placeholder="请选择分类"
              loading={loading}
              size="large"
              showSearch
              optionFilterProp="children"
            >
              {categories.map(category => (
                <Select.Option key={category.id} value={category.id}>
                  {category.name}
                </Select.Option>
              ))}
            </Select>
          </Form.Item>

          <Form.Item
            name="summary"
            label="博客摘要"
            rules={[
              { required: true, message: '请输入博客摘要' },
              { max: 500, message: '摘要不能超过500个字符' }
            ]}
          >
            <TextArea
              placeholder="请输入博客摘要"
              rows={3}
              showCount
              maxLength={500}
            />
          </Form.Item>

          <Form.Item
            name="content"
            label="博客内容"
            rules={[{ required: true, message: '请输入博客内容' }]}
          >
            <WangEditor placeholder="请输入博客内容" />
          </Form.Item>
        </Form>
      </Card>
    </div>
  );
};

export default BlogEditor;
